import { useMemo } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Box } from '@givebutter/primitives/box';
import { Separator } from '@givebutter/primitives/separator';
import { maskDecimal } from '@givebutter/util';
import { faFile, faFileCircleXmark, faQuestionCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchEoyReports } from 'api/contacts/contacts';
import { CopyButton } from 'components/copy-button';
import * as DropdownMenu from 'components/dropdown-menu';
import * as Table from 'components/table';
import * as Tooltip from 'components/tooltip';
import { CurrencySymbol } from 'customHooks/useCurrencySymbol';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentContact } from 'customHooks/useCurrentContact';
import { accountQueryKeys } from 'queries/account-query-keys';
import { ContactEoyReport } from 'types/domain';

const columnHelper = createColumnHelper<ContactEoyReport>();

const ContactSummaries = () => {
    const { data: contact } = useCurrentContact();
    const contactId = contact.id;
    const { accountId } = useCurrentAccount();

    const { data: eoyReports } = useSuspenseQuery({
        queryKey: accountQueryKeys.contactEoyReports(accountId, contactId),
        queryFn: () => fetchEoyReports(accountId, contactId),
        select: ({ eoy_reports }) => eoy_reports,
    });

    const columns = useMemo(() => {
        return [
            columnHelper.accessor('year', {
                header: 'Year',
                enableSorting: false,
                cell: info => {
                    const year = info.getValue();
                    const { view_url } = info.row.original;
                    return view_url ? (
                        <a
                            href={view_url}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="font-medium"
                        >
                            {year}
                        </a>
                    ) : (
                        <p className="font-medium">{year}</p>
                    );
                },
            }),
            columnHelper.accessor('amount', {
                header: 'Total transactions',
                enableSorting: false,
                cell: info => {
                    const { amount, available } = info.row.original;

                    return available ? (
                        <span className="font-medium">
                            <CurrencySymbol />
                            {maskDecimal(amount)}
                        </span>
                    ) : (
                        <span className="font-medium text-gray-500">No transactions</span>
                    );
                },
            }),
            columnHelper.accessor('share_url', {
                header: () => (
                    <div className="flex items-center gap-2">
                        Shareable link
                        <Tooltip.Root>
                            <Tooltip.Trigger>
                                <FontAwesomeIcon icon={faQuestionCircle} size="sm" />
                                <span className="sr-only">Information</span>
                            </Tooltip.Trigger>
                            <Tooltip.Portal>
                                <Tooltip.Content inverse>
                                    <p>
                                        Copy a shareable link to this summary. Links expire after 30
                                        days.
                                    </p>
                                    <Tooltip.Arrow inverse />
                                </Tooltip.Content>
                            </Tooltip.Portal>
                        </Tooltip.Root>
                    </div>
                ),
                enableSorting: false,
                cell: info => {
                    const { share_url, available } = info.row.original;
                    return (
                        available &&
                        share_url && (
                            <CopyButton
                                text={share_url}
                                className="w-auto flex-row-reverse gap-2 rounded-md border border-gray-200"
                            >
                                <span className="text-xs">Copy</span>
                            </CopyButton>
                        )
                    );
                },
            }),
            columnHelper.display({
                id: Table.Constants.Actions,
                cell: info => {
                    const { view_url, download_url, available } = info.row.original;
                    return available ? (
                        <DropdownMenu.Root>
                            <Table.RowDropdownTrigger />
                            <DropdownMenu.Content align="end">
                                <DropdownMenu.AnchorItem
                                    href={view_url ?? ''}
                                    disabled={!view_url}
                                    target="_blank"
                                    rel="noreferrer noopener"
                                >
                                    View
                                </DropdownMenu.AnchorItem>
                                <DropdownMenu.AnchorItem
                                    href={download_url ?? ''}
                                    disabled={!download_url}
                                    target="_self"
                                >
                                    Download
                                </DropdownMenu.AnchorItem>
                            </DropdownMenu.Content>
                        </DropdownMenu.Root>
                    ) : null;
                },
            }),
        ];
    }, []);

    const table = useReactTable({
        data: eoyReports,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getRowId: row => row.year.toString(),
        enableHiding: false,
        enableColumnPinning: false,
    });

    return (
        <Box className="overflow-hidden">
            <div className="flex items-center justify-between p-4">
                <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faFile} />
                    <p className="text-lg font-medium text-gray-700">Summaries</p>
                </div>
            </div>
            <Separator />
            <div className="relative overflow-x-auto">
                <Table.Root variant="bordered" table={table}>
                    <div className="flex flex-col gap-y-4">
                        <Table.Content>
                            <Table.Header />
                            <Table.Body />
                        </Table.Content>
                    </div>
                    <Table.Empty variant={'secondary'}>
                        <Table.EmptyIcon>
                            <FontAwesomeIcon icon={faFileCircleXmark} />
                        </Table.EmptyIcon>
                        <Table.EmptyTitle>No summaries found</Table.EmptyTitle>
                    </Table.Empty>
                </Table.Root>
            </div>
        </Box>
    );
};

export { ContactSummaries };
